/*
 * @Author: Alex
 * @Date: 2023-05-18 11:30:15
 * @Description: 地图输出为 png 等格式
 */
import { BaseMapTool } from './mapToolBase'
import RenderEvent from 'ol/render/Event'
import { ElementHelper } from '../mapUtil'
import { error } from '../console'

interface MapPrintEvent {
  /**
   * 开始输出...
   */
  printing: any
  error: any
  /**
   * 调用放监听事件来接收生成的 图片
   */
  print: any
}

type ImageType = 'image/jpeg' | 'image/png'

export default class MapPrintTool extends BaseMapTool<MapPrintEvent> {
  private _imageType: ImageType = 'image/jpeg'

  private _quality = 0.8

  getMap() {
    return this.map
  }

  /**
   * 输出地图
   * @param {Object} options 选项
   * quality: Number between 0 and 1 indicating the image quality to use for image formats that use lossy compression such as image/jpeg and image/webp
   * printNow: 立即输出
   * @api
   */
  print(options?: {
    imageType?: ImageType
    quality?: number
    size?: number[]
    format?: string
    margin?: number
    orientation?: 'landscape' | 'portrait'
    printNow?: boolean
  }) {
    if (!options) {
      options = {
        imageType: 'image/jpeg',
        quality: 0.8,
        size: [210, 297],
        format: 'a4',
        margin: 10,
        orientation: 'landscape',
        printNow: false
      }
    } else {
      options = Object.assign(
        {
          imageType: 'image/jpeg',
          quality: 0.8,
          size: [210, 297],
          format: 'a4',
          margin: 10,
          orientation: 'landscape',
          printNow: false
        },
        options
      )
    }

    console.log('[print] options: ', options)

    const imageType = options.imageType || this._imageType
    const quality = options.quality || this._quality
    if (this.getMap()) {
      if (!options.printNow) {
        // 延迟一会输出，使用方可以监听事件并做出响应
        this.fire('printing', options)
        setTimeout(() => {
          const tmp = Object.assign({}, options)
          tmp.printNow = true
          this.print(tmp)
        }, 200)
        return
      }
      // Run printing
      this.getMap().once(
        options.printNow ? 'postcompose' : 'rendercomplete',
        event => {
          const canvas = this._getCanvas(event, imageType)
          // Calculate print format
          let size = options?.size ?? [210, 297]
          const format = options?.format ?? 'a4'
          let w, h, position
          let orient = options?.orientation ?? 'landscape'
          const margin = options?.margin ?? 10
          if (canvas) {
            // Calculate size
            if (orient !== 'landscape' && orient !== 'portrait') {
              orient = canvas.width > canvas.height ? 'landscape' : 'portrait'
            }
            if (orient === 'landscape') size = [size[1], size[0]]
            const sc = Math.min(
              (size[0] - 2 * margin) / canvas.width,
              (size[1] - 2 * margin) / canvas.height
            )
            w = sc * canvas.width
            h = sc * canvas.height
            // Image position
            position = [(size[0] - w) / 2, (size[1] - h) / 2]
          }
          // get the canvas image
          let image
          try {
            image = canvas ? canvas.toDataURL(imageType, quality) : null
            // Fire print event
            const e = Object.assign(
              {
                type: 'print',
                print: {
                  format: format,
                  orientation: orient,
                  unit: 'mm',
                  size: size,
                  position: position,
                  imageWidth: w,
                  imageHeight: h
                },
                image: image,
                imageType: imageType,
                quality: quality,
                canvas: canvas
              },
              options
            )
            this.fire('print', e)
          } catch (e) {
            error('[print] error: ', e)
            // Fire error event
            this.fire('error', {
              canvas: canvas
            })
            return
          }
        }
      )
      this.getMap().render()
    }
  }

  /**
   * 将map输出到 canvas
   * @param event 渲染事件
   * @param imageType 图片类型
   * @param canvas 指定canvas
   * @returns
   */
  private _getCanvas(
    event: RenderEvent,
    imageType: ImageType,
    canvas?: HTMLCanvasElement
  ) {
    let ctx: CanvasRenderingContext2D | null

    // Create a canvas if none
    if (!canvas) {
      canvas = document.createElement('canvas')
      // testcode
      canvas.setAttribute('crossOrigin', 'anonymous')
      const size = this.getMap().getSize()
      if (!size) return
      canvas.width = size[0]
      canvas.height = size[1]
      ctx = canvas.getContext('2d')
      if (!ctx) return
      if (/jp.*g$/.test(imageType)) {
        ctx.fillStyle = 'white' // this.get('bgColor')
        ctx.fillRect(0, 0, canvas.width, canvas.height)
      }
    } else {
      ctx = canvas.getContext('2d')
    }
    // ol6+ : create canvas using layer canvas
    this.getMap()
      .getViewport()
      .querySelectorAll('.ol-layers canvas, canvas.ol-fixedoverlay')
      .forEach(ele => {
        const canvasEle = ele as HTMLCanvasElement
        if (canvasEle.width && ctx) {
          ctx.save()
          // opacity
          if ((ele.parentNode as HTMLElement).style.opacity === '0') return
          ctx.globalAlpha =
            parseFloat((ele.parentNode as HTMLElement).style.opacity) || 1
          // Blend mode & filter
          ctx.globalCompositeOperation = ElementHelper.getStyle(
            ele.parentNode,
            'mix-blend-mode'
          )
          ctx.filter = ElementHelper.getStyle(ele.parentNode, 'filter')
          // transform
          let tr =
            ElementHelper.getStyle(ele, 'transform') ||
            ElementHelper.getStyle(ele, '-webkit-transform')
          if (/^matrix/.test(tr)) {
            tr = tr.replace(/^matrix\(|\)$/g, '').split(',')
            tr.forEach((t: string, i: string | number) => {
              tr[i] = parseFloat(t)
            })
            ctx.transform(tr[0], tr[1], tr[2], tr[3], tr[4], tr[5])
            ctx.drawImage(canvasEle, 0, 0)
          } else {
            ctx.drawImage(
              canvasEle,
              0,
              0,
              ElementHelper.getStyle(canvasEle, 'width'),
              ElementHelper.getStyle(canvasEle, 'height')
            )
          }
          ctx.restore()
        }
      })
    return canvas
  }

  protected startInternal(): void {
    throw new Error('Method not implemented.')
  }
  protected stopInternal(): void {
    throw new Error('Method not implemented.')
  }
}
